import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { Button } from '@/components/ui/button';
// DISABLED: AuthButton hidden — auth only accessible via direct URL /auth
// import AuthButton from '@/components/AuthButton';
import { Wrench, Star, CheckCircle, HelpCircle, Eraser, BookOpen, Menu, Layers } from 'lucide-react';
// DISABLED: Premium/auth imports no longer needed in NavBar
// import { useSubscription } from '@/contexts/SubscriptionContext';
// import { useUserRole } from '@/hooks/useUserRole';
import { useFeatureFlag } from '@/contexts/FeatureFlagsContext';
// import { useAuth } from '@/hooks/useAuth';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import logoImage from '@/assets/compresor-logo.jpg';

const NavBar = () => {
  // DISABLED: Premium/auth state no longer needed
  // const { isPremium } = useSubscription();
  // const { isAdmin } = useUserRole();
  // const { user } = useAuth();
  const navigate = useNavigate();
  const backgroundRemovalEnabled = useFeatureFlag('background_removal');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const scrollToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    const section = document.getElementById(sectionId);
    if (section) {
      const navbarHeight = 64;
      const elementPosition = section.offsetTop - navbarHeight - 20;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    } else {
      navigate('/?section=' + sectionId);
    }
  };

  const handleNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <nav className="w-full border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src={logoImage} alt="CompresorJPEG" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation Menu */}
          <div className="hidden md:block">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Button 
                      variant="ghost" 
                      className="text-gray-700 hover:text-blue-600"
                      onClick={() => scrollToSection('how-it-works-section')}
                    >
                      <Wrench className="h-4 w-4 mr-2" />
                      Herramienta
                    </Button>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Button 
                      variant="ghost" 
                      className="text-gray-700 hover:text-blue-600"
                      onClick={() => scrollToSection('ventajas-section')}
                    >
                      <Star className="h-4 w-4 mr-2" />
                      Ventajas
                    </Button>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Button 
                      variant="ghost" 
                      className="text-gray-700 hover:text-blue-600"
                      onClick={() => scrollToSection('caracteristicas-section')}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Características
                    </Button>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {backgroundRemovalEnabled && (
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link to="/quitar-fondo">
                        <Button variant="ghost" className="text-gray-700 hover:text-purple-600">
                          <Eraser className="h-4 w-4 mr-2" />
                          Quitar Fondo
                        </Button>
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/herramientas">
                      <Button variant="ghost" className="text-gray-700 hover:text-blue-600">
                        <Layers className="h-4 w-4 mr-2" />
                        Herramientas
                      </Button>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/blog">
                      <Button variant="ghost" className="text-gray-700 hover:text-blue-600">
                        <BookOpen className="h-4 w-4 mr-2" />
                        Blog
                      </Button>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/faq">
                      <Button variant="ghost" className="text-gray-700 hover:text-blue-600">
                        <HelpCircle className="h-4 w-4 mr-2" />
                        FAQ
                      </Button>
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right side: Mobile hamburger only */}
          {/* DISABLED: AuthButton hidden — admin accesses /auth directly */}
          <div className="flex items-center gap-2">
            {/* <AuthButton isPremium={isPremium} isAdmin={isAdmin} /> */}
            <Button 
              variant="ghost" 
              className="md:hidden" 
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Sheet Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <img src={logoImage} alt="CompresorJPEG" className="h-8 w-auto" />
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col py-2">
            <button
              onClick={() => scrollToSection('how-it-works-section')}
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
            >
              <Wrench className="h-4 w-4" />
              Herramienta
            </button>
            <button
              onClick={() => scrollToSection('ventajas-section')}
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
            >
              <Star className="h-4 w-4" />
              Ventajas
            </button>
            <button
              onClick={() => scrollToSection('caracteristicas-section')}
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
            >
              <CheckCircle className="h-4 w-4" />
              Características
            </button>
            {backgroundRemovalEnabled && (
              <button
                onClick={() => handleNavigate('/quitar-fondo')}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
              >
                <Eraser className="h-4 w-4" />
                Quitar Fondo
              </button>
            )}
            <button
              onClick={() => handleNavigate('/herramientas')}
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
            >
              <Layers className="h-4 w-4" />
              Herramientas
            </button>
            <button
              onClick={() => handleNavigate('/blog')}
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
            >
              <BookOpen className="h-4 w-4" />
              Blog
            </button>
            <button
              onClick={() => handleNavigate('/faq')}
              className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors text-left"
            >
              <HelpCircle className="h-4 w-4" />
              Preguntas Frecuentes
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </nav>
  );
};

export default NavBar;
